import React from 'react';
import { Link } from '@inertiajs/react';
import { LinkIcon } from 'lucide-react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Breadcrumbs,
  Badge,
  Card,
  CardHeader,
  CardBody,
  DescriptionList,
  DataTable,
  Button,
} from '../../../design-system/components';
import { formatCurrency } from '../../../lib/formatters';
import {
  admin_campaigns_path,
  edit_admin_campaign_path,
  admin_affiliate_path,
  links_admin_campaign_path,
} from '../../../lib/routes';

const statusVariant = {
  active: 'success',
  paused: 'warning',
  archived: 'default',
};
const affiliateStatusVariant = {
  approved: 'success',
  pending: 'warning',
  denied: 'danger',
  deactivated: 'default',
};

function commissionDisplay(campaign) {
  if (campaign.commission_type === 'percentage') {
    return `${parseFloat(campaign.commission_rate)}%`;
  }
  return formatCurrency(campaign.commission_rate * 100);
}

function durationDisplay(campaign) {
  switch (campaign.commission_duration_type) {
    case 'one_time':
      return 'One-time';
    case 'recurring':
      return 'Recurring (lifetime)';
    case 'recurring_capped':
      return `Recurring (${campaign.commission_duration_months} months)`;
    default:
      return campaign.commission_duration_type;
  }
}

function payoutHoldDisplay(campaign) {
  const parts = [];
  if (campaign.payout_hold_days > 0) {
    parts.push(`${campaign.payout_hold_days} day${campaign.payout_hold_days !== 1 ? 's' : ''}`);
  }
  if (campaign.payout_hold_cycles > 0) {
    parts.push(
      `${campaign.payout_hold_cycles} billing cycle${campaign.payout_hold_cycles !== 1 ? 's' : ''}`,
    );
  }
  return parts.length > 0 ? parts.join(' + ') : 'None';
}

const affiliateColumns = [
  {
    id: 'name',
    header: 'Name',
    sortable: true,
    render: (row) => (
      <Link
        href={admin_affiliate_path(row.id)}
        className="font-medium text-fg hover:text-fg-secondary"
      >
        {row.name}
      </Link>
    ),
  },
  {
    id: 'email',
    header: 'Email',
    accessor: 'email',
  },
  {
    id: 'slug',
    header: 'Slug',
    render: (row) => <span className="font-mono text-xs text-fg-tertiary">{row.slug}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    render: (row) => (
      <Badge variant={affiliateStatusVariant[row.status] ?? 'default'} size="sm">
        {row.status}
      </Badge>
    ),
  },
];

export default function Show({ campaign, affiliates }) {
  const details = [
    {
      term: 'Status',
      detail: (
        <Badge variant={statusVariant[campaign.status] ?? 'default'}>{campaign.status}</Badge>
      ),
    },
    {
      term: 'Default Campaign',
      detail: campaign.is_default ? 'Yes' : 'No',
    },
    {
      term: 'Commission Type',
      detail: <span className="capitalize">{campaign.commission_type}</span>,
    },
    {
      term: 'Commission Rate',
      detail: commissionDisplay(campaign),
    },
    {
      term: 'Duration',
      detail: durationDisplay(campaign),
    },
    {
      term: 'Cookie Window',
      detail: `${campaign.cookie_window_days} days`,
    },
    {
      term: 'Attribution Model',
      detail: campaign.attribution_model === 'first_touch' ? 'First Touch' : 'Last Touch',
    },
    {
      term: 'Minimum Payout',
      detail: formatCurrency(campaign.min_payout_cents),
    },
    {
      term: 'Payout Hold',
      detail: payoutHoldDisplay(campaign),
    },
    {
      term: 'Coupons',
      detail: campaign.coupon_enabled
        ? `Enabled (${campaign.default_coupon_discount_type === 'percent_off' ? `${campaign.default_coupon_discount_value}% off` : `$${campaign.default_coupon_discount_value} off`})`
        : 'Disabled',
    },
    {
      term: 'Total Affiliates',
      detail: campaign.affiliate_count,
    },
    {
      term: 'Total Commissions',
      detail: formatCurrency(campaign.total_commissions_cents),
    },
  ];

  return (
    <AdminLayout>
      <Breadcrumbs
        items={[
          { label: 'Campaigns', href: admin_campaigns_path() },
          { label: campaign.name },
        ]}
      />
      <PageHeader
        title={campaign.name}
        description="Campaign details and assigned affiliates"
        actions={
          <div className="flex items-center gap-2">
            <Link href={links_admin_campaign_path(campaign.id)}>
              <Button variant="outline">
                <LinkIcon size={16} />
                Invite Links
              </Button>
            </Link>
            <Link href={edit_admin_campaign_path(campaign.id)}>
              <Button variant="outline">Edit</Button>
            </Link>
          </div>
        }
      />

      <div className="mt-6">
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">Campaign Details</h3>
          </CardHeader>
          <CardBody>
            <DescriptionList items={details} columns={3} />
          </CardBody>
        </Card>
      </div>

      <div className="mt-6">
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">
              Assigned Affiliates ({affiliates.length})
            </h3>
          </CardHeader>
          <CardBody className="p-0">
            <DataTable
              columns={affiliateColumns}
              data={affiliates}
              keyAccessor="id"
              emptyTitle="No affiliates assigned"
              emptyDescription="Affiliates will appear here once they are assigned to this campaign."
              className="border-0 rounded-none"
            />
          </CardBody>
        </Card>
      </div>
    </AdminLayout>
  );
}
